/**
 * SQL Service
 *
 * SQL 查詢服務 - 提供 SQL 查詢功能
 * 透過 Supabase RPC 執行 SQL 查詢（相容舊 MySQL 代碼）
 */

import { supabase } from './supabaseClient'

// ==================== 類型定義 ====================

/** 查詢結果 */
export interface QueryResult {
  affectedRows?: number
  insertId?: number
  warningStatus?: number
}

/** 插入結果 */
export interface InsertResult {
  insertId: number | string
  affectedRows: number
}

/** 更新/刪除結果 */
export interface ModifyResult {
  affectedRows: number
}

/** WHERE 條件 */
export type WhereCondition = string | Record<string, unknown>

/** 排序方向 */
export type SortOrder = 'ASC' | 'DESC' | 'asc' | 'desc'

/** RPC 參數映射表 */
type ProcedureParamsMap = Record<string, string[]>

// ==================== 服務定義 ====================

class SQLService {
  private isInitialized: boolean = false

  /**
   * 初始化服務
   */
  async init(): Promise<void> {
    if (this.isInitialized) return
    // Supabase client is initialized on import
    this.isInitialized = true
  }

  /**
   * 執行查詢 (為了相容舊代碼，支援 Raw SQL)
   * 注意：這是過渡方案，應盡量改用 Supabase SDK
   */
  async query(sql: string, params: unknown[] = []): Promise<unknown[] | QueryResult> {
    await this.ensureConnection()

    // 合併 SQL 和參數
    const combinedSql = this.combineQueryAndParams(sql, params)

    // 簡單判斷 SQL 類型
    const trimmedSql = combinedSql.trim().toUpperCase()
    const isSelect =
      trimmedSql.startsWith('SELECT') || trimmedSql.startsWith('WITH') || trimmedSql.startsWith('SHOW')
    const isCall = trimmedSql.startsWith('CALL')

    if (isCall) {
      console.warn('[SQLService] 檢測到 CALL 語句，請改用 callProcedure 方法以獲得更好支援')
      const { error } = await supabase.rpc('exec_cmd', { sql: combinedSql })
      if (error) throw error
      return []
    }

    if (isSelect) {
      const { data, error } = await supabase.rpc('exec_sql', { sql: combinedSql })
      if (error) {
        console.error('[SQLService] Supabase exec_sql 錯誤:', error)
        throw error
      }
      return (data as unknown[]) || []
    } else {
      const { error } = await supabase.rpc('exec_cmd', { sql: combinedSql })
      if (error) {
        console.error('[SQLService] Supabase exec_cmd 錯誤:', error)
        throw error
      }
      // 非 SELECT 查詢通常返回影響行數等，但 exec_cmd 返回 void
      return { affectedRows: 0, insertId: 0, warningStatus: 0 }
    }
  }

  /**
   * 將 SQL 查詢和參數合併成一個字串
   */
  combineQueryAndParams(sql: string, params: unknown[]): string {
    if (!params || params.length === 0) return sql

    let index = 0
    return sql.replace(/\?/g, () => {
      const param = params[index++]
      if (param === null || param === undefined) return 'NULL'
      if (typeof param === 'number') return String(param)
      if (typeof param === 'boolean') return param ? 'TRUE' : 'FALSE'
      if (param instanceof Date) return `'${param.toISOString()}'`
      if (typeof param === 'object') return `'${JSON.stringify(param).replace(/'/g, "''")}'`
      return `'${String(param).replace(/'/g, "''")}'`
    })
  }

  /**
   * SELECT 查詢 - 優先使用 Supabase SDK
   */
  async select(
    table: string,
    columns: string | string[] = '*',
    where: WhereCondition | null = null,
    orderBy: string | null = null,
    limit: number | null = null
  ): Promise<unknown[]> {
    const columnStr = columns === '*' ? '*' : Array.isArray(columns) ? columns.join(',') : columns
    let query = supabase.from(table).select(columnStr)

    if (where) {
      // 簡單的 WHERE 字串無法直接用於 SDK
      if (typeof where === 'string') {
        const result = await this.query(
          `SELECT ${Array.isArray(columns) ? columns.join(', ') : columns} FROM ${table} WHERE ${where} ${orderBy ? 'ORDER BY ' + orderBy : ''} ${limit ? 'LIMIT ' + limit : ''}`
        )
        return result as unknown[]
      } else {
        query = query.match(where)
      }
    }

    if (orderBy) {
      const [col, order] = orderBy.split(/\s+/)
      query = query.order(col, { ascending: order?.toUpperCase() !== 'DESC' })
    }

    if (limit) {
      query = query.limit(limit)
    }

    const { data, error } = await query
    if (error) throw error
    return data || []
  }

  /**
   * INSERT 操作
   */
  async insert(table: string, data: Record<string, unknown> | Record<string, unknown>[]): Promise<InsertResult> {
    const { data: result, error } = await supabase.from(table).insert(data).select()

    if (error) throw error
    const resultArray = result as { id?: string | number }[] | null
    return {
      insertId: resultArray?.[0]?.id || 0,
      affectedRows: resultArray?.length || 0
    }
  }

  /**
   * UPDATE 操作
   */
  async update(
    table: string,
    data: Record<string, unknown>,
    where: WhereCondition
  ): Promise<ModifyResult | unknown[] | QueryResult> {
    // where 必須是物件才能使用 .match()
    if (typeof where === 'string') {
      const setClause = Object.entries(data)
        .map(([k, v]) => `${k} = ${this.combineQueryAndParams('?', [v])}`)
        .join(',')
      return this.query(`UPDATE ${table} SET ${setClause} WHERE ${where}`)
    }

    const { data: result, error } = await supabase.from(table).update(data).match(where).select()

    if (error) throw error
    return { affectedRows: (result as unknown[] | null)?.length || 0 }
  }

  /**
   * DELETE 操作
   */
  async delete(table: string, where: WhereCondition): Promise<ModifyResult | unknown[] | QueryResult> {
    if (typeof where === 'string') {
      return this.query(`DELETE FROM ${table} WHERE ${where}`)
    }

    const { data: result, error } = await supabase.from(table).delete().match(where).select()

    if (error) throw error
    return { affectedRows: (result as unknown[] | null)?.length || 0 }
  }

  /**
   * 執行存儲過程 (映射到 Supabase RPC)
   */
  async callProcedure(procedureName: string, params: unknown[] = []): Promise<unknown[]> {
    await this.ensureConnection()

    // 轉換 procedure 名稱為 snake_case
    const rpcName = procedureName.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase()

    // 映射參數
    const rpcParams = this._mapProcedureParams(procedureName, params)

    const { data, error } = await supabase.rpc(rpcName, rpcParams)

    if (error) {
      console.error(`[SQLService] RPC ${rpcName} 失敗:`, error)
      throw error
    }

    // 返回 [data] 以相容 MySQL 預存程序返回格式
    return [data]
  }

  /**
   * 參數映射表
   */
  private _mapProcedureParams(name: string, params: unknown[]): Record<string, unknown> {
    const map: ProcedureParamsMap = {
      GetOrganizationFields: ['p_organization_id'],
      GetWorkOrderList: [
        'p_organization_id',
        'p_status',
        'p_type',
        'p_field_id',
        'p_limit_count',
        'p_sort_field',
        'p_sort_order'
      ],
      GetWorkOrderStats: ['p_organization_id', 'p_field_id', 'p_start_date', 'p_end_date', 'p_type'],
      GetWorkOrderCountByOrganization: ['p_organization_id', 'p_start_date', 'p_end_date'],
      GetDocumentList: [
        'p_organization_id',
        'p_work_order_id',
        'p_type',
        'p_status',
        'p_field_id',
        'p_start_date',
        'p_end_date',
        'p_schedule_start_date',
        'p_schedule_end_date',
        'p_sort_field',
        'p_sort_order'
      ],
      GetTodayStats: ['p_organization_id'],
      GetDispatchByDate: ['p_organization_id', 'p_start_date', 'p_end_date'],
      GetOpenWorkOrders: ['p_organization_id'],
      GetWorkOrderById: ['p_work_order_id'],
      DeleteWorkOrderComplete: ['p_work_order_id'],
      GetOrganizationFieldsWithUser: ['p_organization_id', 'p_user_id'],
      GetDocumentStats: ['p_organization_id', 'p_field_id', 'p_document_type', 'p_start_date', 'p_end_date']
    }

    const paramNames = map[name]
    if (!paramNames) {
      console.warn(`[SQLService] 未知的預存程序: ${name}，無法映射參數。將使用默認參數名 args。`)
      return {}
    }

    const rpcParams: Record<string, unknown> = {}
    paramNames.forEach((key, index) => {
      rpcParams[key] = params[index] === undefined ? null : params[index]
    })

    return rpcParams
  }

  /**
   * 確保連線
   */
  async ensureConnection(): Promise<void> {
    if (!this.isInitialized) {
      await this.init()
    }
  }

  /**
   * 開始事務 (Supabase 不支援客戶端事務)
   */
  async beginTransaction(): Promise<void> {
    console.warn('[SQLService] Supabase 不支援客戶端事務。請考慮使用 RPC。')
  }

  /**
   * 提交事務
   */
  async commit(): Promise<void> {
    // no-op
  }

  /**
   * 回滾事務
   */
  async rollback(): Promise<void> {
    // no-op
  }

  /**
   * 斷開連線
   */
  disconnect(): void {
    this.isInitialized = false
  }
}

// 創建單例
const sqlService = new SQLService()
export default sqlService
